
import { format } from 'date-fns';
import { nl } from 'date-fns/locale';

/**
 * Format a date string with day name in Dutch
 * @param dateString - Date string to format
 * @returns Formatted date string with day (e.g., "maandag 29 september 2025")
 */
export const formatDateWithDay = (dateString: string): string => {
  if (!dateString) return '-';
  
  try {
    const date = new Date(dateString);
    return format(date, 'EEEE d MMMM yyyy', { locale: nl });
  } catch (error) {
    console.error('Error formatting date with day:', error);
    return dateString;
  }
};

/**
 * Format a date string with day name and time in Dutch
 * @param dateString - Date string to format
 * @returns Formatted date string with day and time (e.g., "maandag 29 september 2025 14:30")
 */
export const formatDateWithDayAndTime = (dateString: string): string => {
  if (!dateString) return '-';
  
  try {
    const date = new Date(dateString);
    return format(date, 'EEEE d MMMM yyyy HH:mm', { locale: nl });
  } catch (error) {
    console.error('Error formatting date with day and time:', error);
    return dateString;
  }
};

/**
 * Format a date string for display (legacy function - now shows day)
 * @param dateString - Date string to format
 * @returns Formatted date string with day and time
 */
export const formatDate = (dateString: string): string => {
  return formatDateWithDayAndTime(dateString);
};

/**
 * Format a date string as short format with day
 * @param dateString - Date string to format
 * @returns Short formatted date string (e.g., "ma 29 sep 2025")
 */
export const formatDateShort = (dateString: string): string => {
  if (!dateString) return '-';
  
  try {
    const date = new Date(dateString);
    return format(date, 'EEE d MMM yyyy', { locale: nl });
  } catch (error) {
    console.error('Error formatting short date:', error);
    return dateString;
  }
};

/**
 * Format a date string as relative time (e.g., "5 minutes ago")
 * @param dateString - Date string to format
 * @returns Formatted relative time string
 */
export const formatRelativeTime = (dateString: string): string => {
  if (!dateString) return '-';
  
  try {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now.getTime() - date.getTime();
    const diffInSecs = Math.floor(diffInMs / 1000);
    const diffInMins = Math.floor(diffInSecs / 60);
    const diffInHours = Math.floor(diffInMins / 60);
    const diffInDays = Math.floor(diffInHours / 24);
    
    if (diffInSecs < 60) {
      return 'zojuist';
    } else if (diffInMins < 60) {
      return `${diffInMins} ${diffInMins === 1 ? 'minuut' : 'minuten'} geleden`;
    } else if (diffInHours < 24) {
      return `${diffInHours} ${diffInHours === 1 ? 'uur' : 'uren'} geleden`;
    } else if (diffInDays < 7) {
      return `${diffInDays} ${diffInDays === 1 ? 'dag' : 'dagen'} geleden`;
    } else {
      return formatDate(dateString);
    }
  } catch (error) {
    console.error('Error formatting relative time:', error);
    return dateString;
  }
};
